import { assertNever } from "@sa-nu/shared-utils/misc";
import clsx from "clsx";
import Image from "next/image";
import { memo, useCallback } from "react";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import type { AccommodationFacilityType } from "../../../../../__generated__/gql/graphql";
import { AccommodationOperationType } from "../../../../../__generated__/gql/graphql";
import { getAccommodationFacilityIconPathsForReservation } from "../../../../utils/accommodation-facility";

const Fragment = graphql(`
  fragment AccommodationOnHeaderCell on Accommodation {
    code
    architectureType {
      size
    }
    facilityTypes
    operationType
  }
`);

// 親から渡る onAccommodationClick(id) を memo 化セル用に id 束縛済みの () => void に変換する。
export default function AccommodationHeaderCell({
  accommodationId,
  accommodationData,
  onAccommodationClick,
}: {
  accommodationId: string;
  accommodationData: FragmentType<typeof Fragment>;
  onAccommodationClick?: (accommodationId: string) => void;
}) {
  const handleClick = useCallback(
    () => onAccommodationClick?.(accommodationId),
    [onAccommodationClick, accommodationId],
  );
  return (
    <AccommodationHeaderCellMemo
      accommodationData={accommodationData}
      onAccommodationClick={onAccommodationClick ? handleClick : undefined}
    />
  );
}

function AccommodationHeaderCellComponent({
  accommodationData,
  onAccommodationClick,
}: {
  accommodationData: FragmentType<typeof Fragment>;
  onAccommodationClick?: () => void;
}) {
  const accommodation = useFragment(Fragment, accommodationData);

  const displayIconPaths = getIconPaths(
    new Set(accommodation.facilityTypes),
    accommodation.architectureType.size,
  );
  const operationTypeLabel = accommodation.operationType
    ? getOperationTypeLabel(accommodation.operationType)
    : null;

  return (
    <div className="border-r-sbeige bg-swhite w-full min-w-0 flex-1 grow border-r p-[2px]">
      <button
        data-analytics-id="reservation_calendar_clicked_accommodation"
        type="button"
        onClick={onAccommodationClick}
        disabled={!onAccommodationClick}
        className={clsx(
          "bg-slightbeige-116 font-roboto inset-ring-sbeige relative flex h-full w-full flex-col items-center rounded-sm inset-ring",
          // 運用タイプのラベルがない場合は、code と icon が中央に来るように justify-center を指定
          !operationTypeLabel && "justify-center",
          onAccommodationClick
            ? "cursor-pointer hover:opacity-60"
            : "cursor-default",
        )}
      >
        {operationTypeLabel && (
          <div className="bg-skogecha-100 h-[10px] w-full shrink-0 truncate rounded-t-sm text-center text-[8px] leading-[10px] font-medium tracking-[0.15px] text-white md:h-[14px] md:text-[10px] md:leading-[14px]">
            {operationTypeLabel}
          </div>
        )}

        <div
          className={clsx(
            "flex w-full flex-col items-center",
            // icon が無いと code だけが label 直下に張り付くので、残り高さの中央に置く。
            displayIconPaths.length === 0 && "flex-1 justify-center",
          )}
        >
          {/* Accommodation code */}
          <span
            className={clsx(
              "text-skogecha w-full truncate px-[2px] text-center leading-[1.5] font-bold tracking-[0.15px] md:text-clip",
              // 5文字以上のcodeを表示する際にテキストサイズを調整する。
              // 5文字以上にしているのはSelection Seriesの長めの名前が収まるようにしたいため。
              // 文字サイズのクラスを両方付けると、記述順でなく生成 CSS の順で効く方が決まるため、どちらか一方だけを付ける。
              accommodation.code.length >= 5
                ? "text-[9px] md:text-[11px]"
                : "text-[11px] md:text-[12px]",
            )}
          >
            {accommodation.code}
          </span>

          {/* Facility icons */}
          {displayIconPaths.length > 0 && (
            <div className="flex items-center justify-center">
              {displayIconPaths.map((iconPath, index) => (
                <Image
                  key={index}
                  src={iconPath}
                  alt="facility"
                  width={16}
                  height={16}
                  className={clsx(
                    "size-[12px]",
                    !operationTypeLabel && "md:size-[16px]",
                  )}
                />
              ))}
            </div>
          )}
        </div>

        {/* クリック可能インジケーター */}
        {onAccommodationClick && (
          <div className="absolute right-[3px] bottom-[3px] size-[6px] md:size-[8px]">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 0V8H0L8 0Z" fill="#FA4616" />
            </svg>
          </div>
        )}
      </button>
    </div>
  );
}

const AccommodationHeaderCellMemo = memo(AccommodationHeaderCellComponent);

export const _getIconPaths = getIconPaths; // for test
export const _getOperationTypeLabel = getOperationTypeLabel; // for test

function getOperationTypeLabel(
  operationType: AccommodationOperationType,
): "Owners" | "Co-Owners" | null {
  switch (operationType) {
    case AccommodationOperationType.FullOwner:
      return "Owners";
    case AccommodationOperationType.CoOwners:
      return "Co-Owners";
    case AccommodationOperationType.Open:
      return null;
    default:
      return assertNever(operationType);
  }
}

/**
 * Get icon paths for display, including size icon if applicable
 * @param facilities Set of facility types
 * @param size The architecture type size (Small, Medium, Large, or null)
 * @returns Array of icon paths in display order
 */
function getIconPaths(
  facilities: Set<AccommodationFacilityType>,
  size: string | null | undefined,
): string[] {
  const facilityIconPaths =
    getAccommodationFacilityIconPathsForReservation(facilities);

  const sizeIconPath = getSizeIconPath(size);

  return sizeIconPath
    ? [sizeIconPath, ...facilityIconPaths]
    : facilityIconPaths;
}

function getSizeIconPath(size: string | null | undefined): string | null {
  switch (size) {
    case "Small":
      return "/i/icon_small_type.svg";
    case "Large":
      return "/i/icon_large_type.svg";
    default:
      return null;
  }
}
